import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react"
import { MarkdownField, type MarkdownFieldHandle } from "./MarkdownField"
import { api, type CommentAttachment } from "../lib/api"
import { useLoad } from "./ui"
import { attachmentRef, resolveAttachmentHref, type AttachmentRef } from "../lib/attachment-refs"
import {
  AttachmentDropZone,
  AttachmentList,
  AttachmentPicker,
  uploadDraft,
  useAttachmentSelection,
} from "./attachments"

type CommentEditorProps = {
  initialBody?: string
  initialAttachments?: CommentAttachment[]
  onSubmit: (body: string, removeAttachmentIds: string[], attachmentIds: string[]) => Promise<void>
  onCancel?: () => void
  placeholder?: string
  compact?: boolean
}

export function CommentEditor({
  initialBody = "",
  initialAttachments = [],
  onSubmit,
  onCancel,
  placeholder = "Write a comment...",
  compact = false,
}: CommentEditorProps) {
  const [body, setBody] = useState(initialBody)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const field = useRef<MarkdownFieldHandle | null>(null)

  // The editor reads its value once, at mount. Bumping this is how the box
  // clears itself after a successful post — there is no imperative setter.
  const [editorKey, setEditorKey] = useState(0)

  // Images uploaded from inside the editor. They are already in the bucket as
  // drafts; posting the comment is what claims them. Their ids are also what
  // the body's `attachment:` refs point at, which is why the list has to
  // survive until submit rather than living in the editor.
  const [drafts, setDrafts] = useState<AttachmentRef[]>([])

  // The picker, the drop zone and the chip list, shared with the task
  // description's editor.
  const selection = useAttachmentSelection({ initialAttachments, onError: setError })

  // Everything an `attachment:` ref in this body could name: what the comment
  // already had, plus what this session uploaded.
  const refs = useMemo<AttachmentRef[]>(
    () => [...initialAttachments, ...drafts],
    [initialAttachments, drafts],
  )

  // Read through a ref: Crepe captures proxyDomURL once per image node view,
  // and a node mounted right after an upload has to resolve the draft that
  // upload just created.
  const refsRef = useRef(refs)
  useEffect(() => {
    refsRef.current = refs
  }, [refs])

  const uploadInline = useCallback(async (file: File) => {
    const draft = await uploadDraft(file)
    // Synchronously as well as through state: Crepe resolves the new image's
    // src as soon as this returns, before the effect that syncs refsRef runs.
    refsRef.current = [...refsRef.current, draft]
    setDrafts((current) => [...current, draft])
    // The document gets the ref, never the signed URL: remark escapes the `&`
    // in a query string, and the URL expires in an hour. proxyDomURL below is
    // what makes the image visible while writing.
    return attachmentRef(draft.id)
  }, [])

  const proxyDomURL = useCallback((url: string) => {
    return resolveAttachmentHref(refsRef.current)(url) ?? url
  }, [])

  // Everyone who can act in this company, for the `@` menu. Fetched once and
  // filtered here: a company roster is small, and a request per keystroke is
  // not. The editor mounts on the first click, so a thread nobody writes in
  // never asks for it.
  const people = useLoad(() =>
    api
      .get<{ employees: Array<{ id: string; name: string }> }>("/auth/employees/roster")
      .catch(() => ({ employees: [] })),
  )
  const roster = people.data?.employees ?? []
  const rosterRef = useRef(roster)
  useEffect(() => {
    rosterRef.current = roster
  }, [roster])

  const searchMentions = useCallback(async (query: string) => {
    const needle = query.trim().toLowerCase()
    const all = rosterRef.current
    if (!needle) return all.slice(0, 8)
    return all
      .filter((person) => person.name.toLowerCase().includes(needle))
      // A name that starts with the query is the one being typed towards.
      .sort((a, b) => {
        const rank = (n: string) => (n.toLowerCase().startsWith(needle) ? 0 : 1)
        return rank(a.name) - rank(b.name) || a.name.localeCompare(b.name)
      })
      .slice(0, 8)
  }, [])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // Read the field directly rather than the debounced onChange, so a submit
    // straight after a keystroke is not a quarter-second stale. What it holds
    // is already what gets stored — the editor's document carries refs, not
    // URLs, so there is nothing to rewrite here.
    const stored = field.current?.getValue() ?? body

    if (!stored.trim() && selection.files.length === 0 && selection.remaining.length === 0) {
      setError("Comment cannot be empty")
      return
    }

    const problem = selection.problem()
    if (problem) {
      setError(problem)
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const picked = await selection.uploadPicked()
      await onSubmit(stored, selection.removeIds, [...drafts.map((draft) => draft.id), ...picked])
      // Reset form after successful submit
      setBody("")
      selection.clear()
      setDrafts([])
      setEditorKey((n) => n + 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit comment")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      {/* Editor area. The drop handlers sit here and stop the event before
          ProseMirror sees it: a file dropped on a comment is an attachment,
          not something to insert into the document. */}
      <AttachmentDropZone dragOver={selection.dragOver} handlers={selection.dropHandlers}>
        <MarkdownField
          key={editorKey}
          initialValue={body}
          handle={field}
          onChange={setBody}
          onUpload={uploadInline}
          proxyDomURL={proxyDomURL}
          onMentionSearch={searchMentions}
          placeholder={placeholder}
          compact
          // A new-comment box starts as a placeholder and mounts the editor on
          // the first click, so a task page with several threads loads none of
          // it. Editing an existing comment already *was* a click, so it opens
          // straight into the editor.
          startEditing={compact}
          ariaLabel="Comment"
        />
      </AttachmentDropZone>

      <AttachmentList
        initialAttachments={initialAttachments}
        removeIds={selection.removeIds}
        files={selection.files}
        previews={selection.previews}
        onToggleExisting={selection.toggleExisting}
        onRemoveFile={selection.removeFile}
      />

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AttachmentPicker onFiles={selection.addFiles} />
        </div>

        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="rounded-md border border-border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-primary px-3 py-1 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? "Posting..." : "Post"}
          </button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </form>
  )
}
